import React, { useState, useEffect, useRef, forwardRef } from "react";
import classNames from "classnames";
import PropTypes from "prop-types";
import styles from "./VideoPlayer.scss";
import { Controls } from "./parts/Controls";
import { Spinner } from "../../loader/Spinner";
import { ElementPositionListener } from "../../utils/ElementPositionListener";

export const VideoPlayer = forwardRef(
    ({ src, contentType, size, autoplay, preload, loop, muted, downloadable, focusX, focusY, controls }, ref) => {
        const playerRef = useRef();
        const controlsVisibleTimeout = useRef();
        const [isPlaying, setIsPlaying] = useState(false);
        const [isMuted, setIsMuted] = useState(autoplay || muted);
        const [isWaiting, setIsWaiting] = useState(true);
        const [manuallyPaused, setManuallyPaused] = useState(false);
        const [inViewport, setInViewport] = useState(false);
        const [seekBarValue, setSeekBarValue] = useState(0);
        const [showControls, setShowControls] = useState(true);
        const [bufferLength, setBufferLength] = useState(null);

        // Handle src change
        useEffect(() => {
            if (src) {
                bindEventListeners();
                playerRef.current.load();
            } else {
                removeEventListeners();
            }
            return () => {
                removeEventListeners();
            };
        }, [src]);

        // Hide controls after inactivity
        useEffect(() => {
            if (showControls) {
                clearTimeout(controlsVisibleTimeout.current);
                controlsVisibleTimeout.current = setTimeout(() => {
                    setShowControls(false);
                }, 6000);
            }
            return () => {
                clearTimeout(controlsVisibleTimeout.current);
            };
        }, [showControls]);

        // Handle autoplay
        useEffect(() => {
            if (inViewport && autoplay && !manuallyPaused) {
                handlePlayVideo();
            } else {
                handlePauseVideo();
            }
        }, [inViewport, autoplay, manuallyPaused]);

        const bindEventListeners = () => {
            const player = playerRef.current;
            if (!player) {
                return;
            }

            player.addEventListener("canplay", onVideoReady);
            player.addEventListener("timeupdate", onTimeUpdate);
            player.addEventListener("pause", onVideoPaused);
            player.addEventListener("volumechange", onVolumeChange);
            player.addEventListener("waiting", onVideoWaiting);
            player.addEventListener("playing", onVideoPlaying);
            player.addEventListener("progress", onVideoProgress);
        };

        const removeEventListeners = () => {
            const player = playerRef.current;
            if (!player) {
                return;
            }

            player.removeEventListener("canplay", onVideoReady);
            player.removeEventListener("timeupdate", onTimeUpdate);
            player.removeEventListener("pause", onVideoPaused);
            player.removeEventListener("volumechange", onVolumeChange);
            player.removeEventListener("waiting", onVideoWaiting);
            player.removeEventListener("playing", onVideoPlaying);
            player.removeEventListener("progress", onVideoProgress);
        };

        const onVideoReady = () => {
            setIsWaiting(false);
        };

        const onVideoPaused = () => {
            setIsPlaying(false);
        };

        const onVolumeChange = () => {
            setIsMuted(playerRef.current.muted);
        };

        const onTimeUpdate = () => {
            const player = playerRef.current;
            if (player) {
                let value = (100 / player.duration) * player.currentTime;
                setSeekBarValue(value);
            }
        };

        const onVideoWaiting = () => {
            setIsWaiting(true);
        };

        const onVideoPlaying = () => {
            setShowControls(true);
            setIsPlaying(true);
            setIsWaiting(false);
        };

        const onVideoProgress = () => {
            const player = playerRef.current;
            if (player) {
                const ranges = player.buffered;
                if (!ranges || !ranges.length) {
                    setBufferLength(null);
                } else {
                    const secondsTotal = player.duration;
                    const secondsEnd = ranges.end(ranges.length - 1);
                    setBufferLength((secondsEnd / secondsTotal) * 100);
                }
            }
        };

        const handleSeekVideo = (value) => {
            setBufferLength(null);
            setSeekBarValue(value);
            const player = playerRef.current;
            if (player) {
                player.currentTime = player.duration * (value / 100);
            }
        };

        const handlePlayVideo = () => {
            const player = playerRef.current;
            player.play().catch((err) => {
                console.warn("Video play interrupted.", err);
            });
        };

        const handlePauseVideo = () => {
            playerRef.current.pause();
        };

        const handleMuteVideo = (muted) => {
            playerRef.current.muted = muted;
            setIsMuted(muted);
        };

        const handleSetFullscreen = () => {
            const player = playerRef.current;
            if (player) {
                if (player.requestFullscreen) {
                    player.requestFullscreen();
                } else if (player.msRequestFullscreen) {
                    player.msRequestFullscreen();
                } else if (player.mozRequestFullScreen) {
                    player.mozRequestFullScreen();
                } else if (player.webkitRequestFullscreen) {
                    player.webkitRequestFullscreen();
                } else if (player.webkitEnterFullScreen) {
                    player.webkitEnterFullScreen();
                }
            }
        };

        return (
            <div
                ref={ref}
                className={classNames(styles.base, {
                    [styles.fill]: size === "fill",
                    [styles.fit]: size === "fit"
                })}
                style={{
                    width: typeof size === "number" ? `${size}px` : null,
                    height: typeof size === "number" ? `${size}px` : null
                }}
            >
                {/* Wrapper */}
                <div className={styles.wrapper}>
                    {/* Click area*/}
                    <div
                        className={styles.clickArea}
                        onClick={() => setShowControls(true)}
                        onMouseMove={() => setShowControls(true)}
                    />

                    {/* Loading */}
                    {isWaiting ? (
                        <div className={styles.spinner}>
                            <Spinner large />
                        </div>
                    ) : null}

                    {/* Video controls */}
                    <Controls
                        visible={src && controls && showControls}
                        onPlay={handlePlayVideo}
                        onPause={() => {
                            setManuallyPaused(true);
                            handlePauseVideo();
                        }}
                        onMute={handleMuteVideo}
                        setFullscreen={handleSetFullscreen}
                        seekBarValue={seekBarValue}
                        setSeekBarValue={handleSeekVideo}
                        isMuted={isMuted}
                        isPlaying={isPlaying}
                        bufferLength={bufferLength}
                    />

                    {/* Video */}
                    <video
                        ref={playerRef}
                        preload={preload}
                        loop={loop}
                        playsInline
                        muted={isMuted}
                        className={styles.video}
                        style={
                            focusX && focusY
                                ? {
                                      objectPosition: `${focusX}% ${focusY}%`
                                  }
                                : null
                        }
                        controlsList={!downloadable ? "nodownload" : null}
                    >
                        <source src={src} type={contentType} />
                    </video>
                </div>

                {/* Check when video is visible in viewport */}
                <ElementPositionListener
                    onEnterViewport={() => {
                        setInViewport(true);
                    }}
                    onExitViewport={() => {
                        setInViewport(false);
                    }}
                />
            </div>
        );
    }
);

VideoPlayer.defaultProps = {
    preload: "metadata",
    downloadable: false,
    controls: true
};

VideoPlayer.propTypes = {
    /**
     * Video file URL
     */
    src: PropTypes.string,

    /**
     * Size of video
     */
    size: PropTypes.oneOf(["fit"]),

    /**
     * Controls video playback
     */
    autoplay: PropTypes.bool,

    /**
     * Focus point X at percent of video
     */
    focusX: PropTypes.number,

    /**
     * Focus point Y at percent of video
     */
    focusY: PropTypes.number,

    /**
     * Display controls
     */
    controls: PropTypes.bool
};
